import { toggleTagSelection } from 'features/timeSeries/components/panes/tagFilters'
import { MAX_PENS } from 'features/timeSeries/config/constants'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import { buildTagMentionIndex } from './tagMentions'

const EMPTY_INDEX: ReadonlyMap<string, string> = new Map()
const TagMentionIndex = createContext<ReadonlyMap<string, string>>(EMPTY_INDEX)

/**
 * Builds the site's tag-mention index once for the whole tab. Every reply
 * and every tool card reads it from here; building a 6,400-entry map per
 * component was the alternative.
 */
export const TagMentionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { sensorEntries } = useScatterplotContext()
  const index = useMemo(
    () => buildTagMentionIndex(sensorEntries),
    [sensorEntries]
  )
  return React.createElement(
    TagMentionIndex.Provider,
    { value: index },
    children
  )
}

/**
 * What a mentioned tag can do from the chat: hovering one that is on the chart
 * highlights its pen (the others dim, as from the tag legend), clicking one
 * that is not adds it. The highlight is released on leave and on unmount, and
 * only if it is still the one this surface set.
 */
export const useTagMention = () => {
  const {
    sensorEntries,
    selectedSensors,
    setSelectedSensors,
    setHighlightedSensorId,
  } = useScatterplotContext()
  const owned = useRef<string | null>(null)

  const index = useContext(TagMentionIndex)
  const onChart = useMemo(
    () => new Set(selectedSensors.map((sensor) => sensor.id)),
    [selectedSensors]
  )

  const release = useCallback(() => {
    const id = owned.current
    owned.current = null
    if (id === null) return
    setHighlightedSensorId((current) => (current === id ? null : current))
  }, [setHighlightedSensorId])
  useEffect(() => release, [release])

  const hover = useCallback(
    (id: string) => {
      if (!onChart.has(id)) return
      owned.current = id
      setHighlightedSensorId(id)
    },
    [onChart, setHighlightedSensorId]
  )

  const add = useCallback(
    (id: string) => {
      const entry = sensorEntries.find((sensor) => sensor.id === id)
      if (!entry || onChart.has(id)) return
      setSelectedSensors((prev) => toggleTagSelection(prev, entry, MAX_PENS))
    },
    [onChart, sensorEntries, setSelectedSensors]
  )

  return {
    index,
    isOnChart: (id: string) => onChart.has(id),
    hover,
    release,
    add,
  }
}
